import React, { useEffect, useState, useCallback } from 'react';
import { companyAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import BackButton from '../components/BackButton';
import EntrySheet from '../components/EntrySheet';
import { fmtDate } from '../utils/date';

const CATEGORIES = ['general', 'rent', 'salaries', 'utilities', 'marketing', 'transport', 'maintenance', 'other'];
const EMPTY_FORM = { category: 'general', amount: '', description: '', supplier: '', expense_at: '' };

const fmt = (n) =>
  new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

export default function CompanyExpenses() {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const [businesses, setBusinesses] = useState([]);
  const [selectedBiz, setSelectedBiz] = useState(null);
  const [bizData, setBizData]   = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [editEntry, setEditEntry]   = useState(null);
  const [editForm, setEditForm]     = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError]   = useState('');

  useEffect(() => {
    companyAPI.listBiz().then(r => setBusinesses(r.data.businesses || []));
  }, []);

  const loadBiz = useCallback(async (biz) => {
    setSelectedBiz(biz);
    setShowForm(false);
    setForm(EMPTY_FORM);
    setError('');
    setLoading(true);
    try {
      const r = await companyAPI.getBusinessDetail(biz.id);
      setBizData(r.data);
    } catch {
      setBizData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) { setError('Enter a valid positive amount'); return; }
    setSaving(true);
    try {
      await companyAPI.addBizExpense(selectedBiz.id, {
        category:    form.category,
        amount,
        description: form.description || undefined,
        supplier:    form.supplier    || undefined,
        expense_at:  form.expense_at ? new Date(form.expense_at).toISOString() : undefined,
      });
      setForm(EMPTY_FORM);
      setShowForm(false);
      await loadBiz(selectedBiz);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to record expense');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (exp) => {
    setEditEntry(exp);
    setEditForm({
      category:    exp.category,
      amount:      String(parseFloat(Number(exp.amount).toFixed(2))),
      description: exp.description || '',
      supplier:    exp.supplier || '',
      expense_at:  exp.expense_at ? new Date(exp.expense_at).toISOString().slice(0, 16) : '',
    });
    setEditError('');
  };

  const handleEditSave = async () => {
    setEditError('');
    const amount = parseFloat(editForm.amount);
    if (!amount || amount <= 0) { setEditError('Enter a valid amount'); return; }
    setEditSaving(true);
    try {
      await companyAPI.updateBizExpense(selectedBiz.id, editEntry.id, {
        category:    editForm.category,
        amount,
        description: editForm.description || undefined,
        supplier:    editForm.supplier || undefined,
        expense_at:  editForm.expense_at ? new Date(editForm.expense_at).toISOString() : undefined,
      });
      setEditEntry(null);
      await loadBiz(selectedBiz);
    } catch (err) {
      setEditError(err?.response?.data?.error || 'Failed to update');
    } finally {
      setEditSaving(false);
    }
  };

  const handleEditDelete = async () => {
    setEditSaving(true);
    try {
      await companyAPI.voidBizExpense(selectedBiz.id, editEntry.id);
      setEditEntry(null);
      await loadBiz(selectedBiz);
    } catch (err) {
      setEditError(err?.response?.data?.error || 'Failed to delete');
    } finally {
      setEditSaving(false);
    }
  };

  const expenses = bizData?.expenses || [];
  const currency = selectedBiz?.currency || '';
  const total    = expenses.reduce((s, e) => s + Number(e.amount), 0);

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '1.5rem 1rem 4rem' }}>
      <BackButton />
      <h1 style={{ margin: '0 0 1.2rem', fontSize: '1.4rem', fontWeight: 700 }}>Company Expenses</h1>

      {/* Business selector */}
      <div style={{ marginBottom: '1.2rem' }}>
        <label style={{ fontSize: '0.82rem', color: 'var(--color-muted)', display: 'block', marginBottom: 4 }}>
          Select business
        </label>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {businesses.map(b => (
            <button
              key={b.id}
              onClick={() => loadBiz(b)}
              style={{
                padding: '0.4rem 0.9rem', borderRadius: 20, border: '1px solid var(--color-border)',
                background: selectedBiz?.id === b.id ? 'var(--color-primary)' : 'transparent',
                color: selectedBiz?.id === b.id ? '#fff' : 'inherit',
                cursor: 'pointer', fontSize: '0.88rem', fontWeight: 500,
              }}
            >
              {b.name}
            </button>
          ))}
        </div>
      </div>

      {selectedBiz && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <div>
              <span style={{ fontSize: '0.9rem', color: 'var(--color-muted)' }}>{selectedBiz.name} · {currency}</span>
              {expenses.length > 0 && (
                <span style={{ marginLeft: '0.75rem', fontWeight: 700, color: 'var(--color-danger)' }}>
                  {currency} {fmt(total)}
                </span>
              )}
            </div>
            <button
              onClick={() => { setShowForm(s => !s); setError(''); }}
              style={{
                background: 'var(--color-primary)', color: '#fff', border: 'none',
                borderRadius: 8, padding: '0.45rem 1rem', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem',
              }}
            >
              {showForm ? 'Cancel' : '+ Add Expense'}
            </button>
          </div>

          {showForm && (
            <form onSubmit={handleSubmit} style={{
              background: 'var(--color-card)', borderRadius: 12, padding: '1.2rem',
              marginBottom: '1.2rem', display: 'flex', flexDirection: 'column', gap: '0.85rem',
            }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: '0.82rem', color: 'var(--color-muted)' }}>Category</span>
                <select
                  value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  style={{ padding: '0.55rem', borderRadius: 8, border: '1px solid var(--color-border)' }}
                >
                  {CATEGORIES.map(c => (
                    <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                  ))}
                </select>
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: '0.82rem', color: 'var(--color-muted)' }}>Amount ({currency})</span>
                <input
                  type="number" min="0.01" step="any" required value={form.amount}
                  onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                  placeholder="0.00"
                  style={{ padding: '0.55rem', borderRadius: 8, border: '1px solid var(--color-border)' }}
                />
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: '0.82rem', color: 'var(--color-muted)' }}>Description <span style={{ fontStyle: 'italic' }}>(optional)</span></span>
                <input
                  type="text" value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="e.g. January rent"
                  style={{ padding: '0.55rem', borderRadius: 8, border: '1px solid var(--color-border)' }}
                />
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: '0.82rem', color: 'var(--color-muted)' }}>Supplier <span style={{ fontStyle: 'italic' }}>(optional)</span></span>
                <input
                  type="text" value={form.supplier}
                  onChange={e => setForm(f => ({ ...f, supplier: e.target.value }))}
                  placeholder="e.g. KPLC"
                  style={{ padding: '0.55rem', borderRadius: 8, border: '1px solid var(--color-border)' }}
                />
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: '0.82rem', color: 'var(--color-muted)' }}>Date <span style={{ fontStyle: 'italic' }}>(optional)</span></span>
                <input
                  type="datetime-local" value={form.expense_at}
                  onChange={e => setForm(f => ({ ...f, expense_at: e.target.value }))}
                  style={{ padding: '0.55rem', borderRadius: 8, border: '1px solid var(--color-border)' }}
                />
              </label>

              {error && <div style={{ color: 'var(--color-danger)', fontSize: '0.88rem' }}>{error}</div>}

              <button
                type="submit" disabled={saving}
                style={{
                  background: 'var(--color-primary)', color: '#fff', border: 'none',
                  borderRadius: 8, padding: '0.65rem', fontWeight: 600,
                  cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1,
                }}
              >
                {saving ? 'Saving…' : 'Record Expense'}
              </button>
            </form>
          )}

          {loading ? (
            <div style={{ color: 'var(--color-muted)', textAlign: 'center', marginTop: '2rem' }}>Loading…</div>
          ) : expenses.length === 0 ? (
            <div style={{ color: 'var(--color-muted)', textAlign: 'center', marginTop: '2rem' }}>
              No expenses yet for {selectedBiz.name}.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {expenses.map(exp => (
                <div key={exp.id} onClick={isOwner ? () => openEdit(exp) : undefined} style={{
                  background: 'var(--color-card)', borderRadius: 10, padding: '0.85rem 1rem',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  cursor: isOwner ? 'pointer' : 'default',
                }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                      {exp.category.charAt(0).toUpperCase() + exp.category.slice(1)}
                    </div>
                    <div style={{ fontSize: '0.82rem', color: 'var(--color-muted)' }}>
                      {exp.description ? `${exp.description} · ` : ''}{fmtDate(exp.expense_at)}
                      {exp.supplier ? ` · ${exp.supplier}` : ''}
                    </div>
                  </div>
                  <div style={{ fontWeight: 700, color: 'var(--color-danger)', whiteSpace: 'nowrap' }}>
                    {currency} {fmt(exp.amount)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {isOwner && <EntrySheet
        open={!!editEntry}
        onClose={() => setEditEntry(null)}
        title="Edit Expense"
        fields={[
          { key: 'category', label: 'Category', options: ['general','rent','salaries','utilities','marketing','transport','maintenance','other'].map(c => ({ value: c, label: c.charAt(0).toUpperCase() + c.slice(1) })) },
          { key: 'amount',      label: `Amount (${currency})`, type: 'number', required: true },
          { key: 'description', label: 'Description' },
          { key: 'supplier',    label: 'Supplier' },
          { key: 'expense_at',  label: 'Date', type: 'datetime-local' },
        ]}
        values={editForm}
        onChange={(k, v) => setEditForm(f => ({ ...f, [k]: v }))}
        onSave={handleEditSave}
        onDelete={handleEditDelete}
        saving={editSaving}
        error={editError}
      />}
    </div>
  );
}
